import { paths } from 'src/routes/paths';

export const REPORT_FOLDER_QUERY_PARAM = 'folder';

/** Jasper flow query keys that must not be forwarded to the app URL. */
const JASPER_FLOW_RESERVED_KEYS = new Set(['_flowId', 'reportUnit']);

/** App query keys that must not be sent as Visualize.js report parameters. */
export const REPORT_APP_RESERVED_QUERY_KEYS = new Set(['type', REPORT_FOLDER_QUERY_PARAM]);

/**
 * Whether a Jasper resource URI should open under /dashboards.
 * @param {string} reportUri
 * @returns {boolean}
 */
export function isDashboardResourceUri(reportUri) {
  const normalized = normalizeResourceUri(reportUri);
  const firstSegment = normalized.split('/').filter(Boolean)[0] || '';
  return firstSegment.toLowerCase() === 'dashboards';
}

/**
 * Parse a Jasper Server flow.html hyperlink.
 * @param {string} href
 * @returns {{ reportUri: string, params: URLSearchParams } | null}
 */
export function parseJasperFlowUrl(href) {
  if (!href || typeof href !== 'string') {
    return null;
  }

  let url;
  try {
    url = new URL(href, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
  } catch {
    return null;
  }

  const pathname = url.pathname || '';
  if (!pathname.includes('flow.html')) {
    return null;
  }

  const reportUnit = url.searchParams.get('reportUnit');
  if (!reportUnit) {
    return null;
  }

  const reportUri = normalizeResourceUri(reportUnit);
  const params = new URLSearchParams();

  url.searchParams.forEach((value, key) => {
    if (!JASPER_FLOW_RESERVED_KEYS.has(key)) {
      params.append(key, value);
    }
  });

  return { reportUri, params };
}

/**
 * Build an in-app report/dashboard URL from a Jasper flow.html href.
 * @param {string} href
 * @returns {string | null}
 */
export function buildAppUrlFromJasperHref(href) {
  const parsed = parseJasperFlowUrl(href);
  if (!parsed) {
    return null;
  }

  const { reportUri, params } = parsed;
  const basePath = isDashboardResourceUri(reportUri)
    ? paths.dashboards.view(reportUri)
    : paths.reports.view(reportUri);

  const query = params.toString();
  return query ? `${basePath}?${query}` : basePath;
}

/**
 * Convert app URL search params into Visualize.js report params
 * (each value as a string array). Skips reserved app keys.
 * @param {URLSearchParams} searchParams
 * @returns {Record<string, string[]>}
 */
export function buildVisualizeParamsFromSearchParams(searchParams) {
  /** @type {Record<string, string[]>} */
  const result = {};

  if (!searchParams) {
    return result;
  }

  searchParams.forEach((value, key) => {
    if (REPORT_APP_RESERVED_QUERY_KEYS.has(key)) {
      return;
    }

    if (!result[key]) {
      result[key] = [];
    }
    result[key].push(value);
  });

  return result;
}

/**
 * Copy reserved app query keys from an existing search params object.
 * @param {URLSearchParams | null | undefined} existingSearchParams
 * @param {URLSearchParams} target
 */
function appendReservedAppQueryKeys(existingSearchParams, target) {
  if (!existingSearchParams) {
    return;
  }

  REPORT_APP_RESERVED_QUERY_KEYS.forEach((key) => {
    existingSearchParams.getAll(key).forEach((value) => {
      target.append(key, value);
    });
  });
}

/**
 * Convert Visualize.js params into app URL search params.
 * Preserves reserved app keys (`type`, `folder`) from the existing URL;
 * replaces all other keys with the provided report/dashboard params.
 * @param {Record<string, string[]|string|null|undefined>|null|undefined} visualizeParams
 * @param {URLSearchParams | null | undefined} existingSearchParams
 * @returns {URLSearchParams}
 */
export function buildSearchParamsFromVisualizeParams(visualizeParams, existingSearchParams) {
  const next = new URLSearchParams();
  appendReservedAppQueryKeys(existingSearchParams, next);

  if (!visualizeParams || typeof visualizeParams !== 'object') {
    return next;
  }

  Object.keys(visualizeParams)
    .sort()
    .forEach((key) => {
      if (REPORT_APP_RESERVED_QUERY_KEYS.has(key)) {
        return;
      }

      const raw = visualizeParams[key];
      const values = Array.isArray(raw) ? raw : raw == null ? [] : [raw];

      values.forEach((value) => {
        if (value !== null && value !== undefined) {
          next.append(key, String(value));
        }
      });
    });

  return next;
}

/**
 * Keep only reserved app query keys (`type`, `folder`), dropping report filters.
 * @param {URLSearchParams | null | undefined} existingSearchParams
 * @returns {URLSearchParams}
 */
export function clearReportFilterSearchParams(existingSearchParams) {
  const next = new URLSearchParams();
  appendReservedAppQueryKeys(existingSearchParams, next);
  return next;
}

/**
 * Read the selected repository folder path from URL search params.
 * @param {URLSearchParams} searchParams
 * @returns {string|null}
 */
export function parseFolderPathFromParams(searchParams) {
  const value = searchParams.get(REPORT_FOLDER_QUERY_PARAM);
  if (!value) {
    return null;
  }

  return value.startsWith('/') ? value : `/${value}`;
}

/**
 * Build expanded-folder map from a folder path (ancestors + folder).
 * @param {string|null} folderPath
 * @returns {Record<string, boolean>}
 */
export function folderPathToExpandedMap(folderPath) {
  if (!folderPath) {
    return {};
  }

  return expandToFolder({}, folderPath);
}

/**
 * Build ancestor folder paths for expanding the tree to a target folder.
 * @param {string} folderPath - e.g. "/Reports/Sales"
 * @returns {string[]}
 */
export function getAncestorPaths(folderPath) {
  if (!folderPath) {
    return [];
  }

  const parts = folderPath.split('/').filter(Boolean);
  const ancestors = [];
  let current = '';

  parts.forEach((part) => {
    current += `/${part}`;
    ancestors.push(current);
  });

  return ancestors;
}

/**
 * Build the in-app URL for viewing a report (optionally in a new tab).
 * @param {string} uri
 * @param {string} [resourceType='reportUnit']
 * @returns {string}
 */
export function buildReportViewUrl(uri, resourceType = 'reportUnit') {
  const params = new URLSearchParams();
  params.set('type', resourceType);

  const reportFolderPath = extractFolderPathFromReportUri(uri);
  if (reportFolderPath) {
    params.set(REPORT_FOLDER_QUERY_PARAM, reportFolderPath);
  }

  const basePath =
    resourceType === 'dashboard' ? paths.dashboards.view(uri) : paths.reports.view(uri);

  const query = params.toString();
  return query ? `${basePath}?${query}` : basePath;
}

/**
 * Extract the folder path from a report URI.
 * @param {string} uri - e.g. "/Reports/Sales/MyReport"
 * @returns {string|null}
 */
/**
 * Normalize JSR resource URIs for consistent map lookups (leading slash, collapse slashes).
 * @param {string} uri
 * @returns {string}
 */
export function normalizeResourceUri(uri) {
  if (!uri || typeof uri !== 'string') {
    return '';
  }

  let s = uri.trim();
  if (!s.startsWith('/')) {
    s = `/${s}`;
  }

  return s.replace(/\/{2,}/g, '/') || '/';
}

/**
 * Find a report entry in the repository hierarchy by URI.
 * @param {Array} hierarchy
 * @param {string} uri
 * @returns {{ uri: string, label: string, description?: string, resourceType?: string }|null}
 */
export function findReportByUri(hierarchy, uri) {
  if (!uri || !hierarchy?.length) {
    return null;
  }

  const normalizedUri = normalizeResourceUri(uri);

  const traverse = (folders) => {
    for (const folder of folders) {
      for (const report of folder.reports || []) {
        if (normalizeResourceUri(report.uri) === normalizedUri) {
          return report;
        }
      }

      if (folder.children?.length) {
        const found = traverse(folder.children);
        if (found) {
          return found;
        }
      }
    }

    return null;
  };

  return traverse(hierarchy);
}

const USER_FOLDERS_ROOT = '/userFolders';

/**
 * Whether a report URI lives under the JSR User Folders hierarchy.
 * @param {string} uri
 * @returns {boolean}
 */
export function isUserFolderReport(uri) {
  const normalizedUri = normalizeResourceUri(uri);
  return (
    normalizedUri === USER_FOLDERS_ROOT ||
    normalizedUri.startsWith(`${USER_FOLDERS_ROOT}/`)
  );
}

export function extractFolderPathFromReportUri(uri) {
  if (!uri || typeof uri !== 'string') {
    return null;
  }

  const parts = uri.split('/').filter(Boolean);
  if (parts.length <= 1) {
    return null;
  }

  return `/${parts.slice(0, -1).join('/')}`;
}

/**
 * Find a folder node in the hierarchy by path.
 * @param {Array} hierarchy
 * @param {string} targetPath
 * @returns {object|null}
 */
export function findFolderByPath(hierarchy, targetPath) {
  for (const folder of hierarchy) {
    if (folder.path === targetPath) {
      return folder;
    }

    if (folder.children?.length) {
      const found = findFolderByPath(folder.children, targetPath);
      if (found) {
        return found;
      }
    }
  }

  return null;
}

/**
 * Collect reports from a folder and all descendant folders (depth-first).
 * @param {object|null|undefined} folder
 * @returns {Array}
 */
export function collectReportsFromFolder(folder) {
  if (!folder) {
    return [];
  }

  const reports = [...(folder.reports || [])];

  (folder.children || []).forEach((child) => {
    reports.push(...collectReportsFromFolder(child));
  });

  return reports;
}

/**
 * Remove folders that have no reports in themselves or any descendant.
 * @param {Array} hierarchy
 * @returns {Array}
 */
export function pruneEmptyFolders(hierarchy) {
  if (!hierarchy?.length) {
    return [];
  }

  return hierarchy
    .map((folder) => {
      const children = pruneEmptyFolders(folder.children || []);
      const reports = folder.reports || [];

      if (reports.length === 0 && children.length === 0) {
        return null;
      }

      return {
        ...folder,
        children,
      };
    })
    .filter(Boolean);
}

/**
 * Flatten all reports from a repository hierarchy.
 * @param {Array} hierarchy
 * @returns {Array}
 */
export function flattenAllReports(hierarchy) {
  if (!hierarchy?.length) {
    return [];
  }

  return hierarchy.flatMap((folder) => collectReportsFromFolder(folder));
}

/**
 * Merge ancestor paths into an expanded-folders map.
 * @param {Record<string, boolean>} expandedFolders
 * @param {string} folderPath
 * @returns {Record<string, boolean>}
 */
export function expandToFolder(expandedFolders, folderPath) {
  const next = { ...expandedFolders };

  getAncestorPaths(folderPath).forEach((path) => {
    next[path] = true;
  });

  return next;
}
